import React from "react"

import { useTranslation } from "react-i18next"

import { AdminPlayerBonus2 } from "services/bonus/type"
import { GameInterface } from "services/game/type"

import { formatDateTime, formatTournamentMoney } from "util/formatter"

import { getDisplayName, getPlayerBonusStatusColor } from "../../helpers/bonus2.helpers"

import { FreeSpinsSection } from "./FreeSpinsSection"

interface PlayerBonusCardProps {
  playerBonus: AdminPlayerBonus2
  gamesCache: Record<string, GameInterface>
  onViewHistory: (playerBonus: AdminPlayerBonus2) => void
  /** Absent when the viewer may read this player's bonuses but not act on them. */
  onCancel?: (playerBonus: AdminPlayerBonus2) => void
  onActivate?: (playerBonus: AdminPlayerBonus2) => void
}

export const PlayerBonusCard: React.FC<PlayerBonusCardProps> = ({ playerBonus, gamesCache, onViewHistory, onCancel, onActivate }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const wageringPercent =
    Number(playerBonus.wagering_required) > 0 ? (Number(playerBonus.wagering_completed) / Number(playerBonus.wagering_required)) * 100 : 0
  const getStatusColor = getPlayerBonusStatusColor

  // PlayerBonus amounts are NATIVE minor units in the bonus's OWN coin (not USD). Render with
  // formatTournamentMoney so the symbol matches the coin (PLN→zł, CAD→C$) and a 0 shows as
  // "zł0.00" rather than blank — formatFiatCurrency treated the whole value as USD and dropped 0.
  const money = (value?: string | number | null) => formatTournamentMoney(value, playerBonus.coin)

  return (
    <div className="bg-[#1A1A2E] border border-gray-700 rounded-lg p-4 hover:border-purple transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="text-white font-semibold text-lg">{getDisplayName(playerBonus.bonus.name)}</h4>
          <span className={`inline-block px-2 py-1 text-xs rounded text-white mt-1 ${getStatusColor(playerBonus.status)}`}>
            {playerBonus.status}
          </span>
          {/* A Deposit + Spins offer grants two bonuses from one template, so both cards carry
              the same name. Name the halves or they read as a duplicate. */}
          {playerBonus.component === "FREE_SPINS" && (
            <span className="inline-block px-2 py-1 text-xs rounded text-white mt-1 ml-2 bg-purple">{t("bonus2.players.freeSpins2")}</span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onViewHistory(playerBonus)}
            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
          >
            {t("bonus2.players.history")}
          </button>
          {playerBonus.status === "SUBSCRIBED" && onActivate && (
            <button
              onClick={() => onActivate(playerBonus)}
              className="px-3 py-1 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 transition-colors"
            >
              {t("bonus2.players.activate")}
            </button>
          )}
          {/* Iteration-3 Fix 03 — allow unsubscribing a SUBSCRIBED (not-yet-active) bonus,
              not only cancelling an ACTIVE one. Backend cancelPlayerBonus handles both. */}
          {(playerBonus.status === "ACTIVE" || playerBonus.status === "SUBSCRIBED") && onCancel && (
            <button
              onClick={() => onCancel(playerBonus)}
              className="px-3 py-1 bg-rose-500 text-white text-sm rounded hover:bg-rose-700 transition-colors"
            >
              {playerBonus.status === "SUBSCRIBED" ? "Unsubscribe" : "Cancel"}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        {/* These are THIS bonus session's own counters (bonus-service PlayerBonus), NOT the
            player's overall wallet. The real withdrawable balance lives in financial-service and
            is shown on the wallet/user page, not here. Both zero out when the bonus completes
            (summed and paid to the wallet) or is cancelled (bonus forfeited, session cash returned). */}
        <div>
          <div
            className="text-gray-400 mb-1"
            title={t("bonus2.players.virtualBonusMoneyRemaining")}
          >
            {t("bonus2.players.bonusBalance")} <span className="text-gray-500">(session)</span>
          </div>
          <div className="text-white font-semibold">{money(playerBonus.bonus_balance)}</div>
        </div>

        <div>
          <div
            className="text-gray-400 mb-1"
            title={t("bonus2.players.cashLockedInsideThis")}
          >
            {t("bonus2.players.sessionCash")} <span className="text-gray-500">(not wallet)</span>
          </div>
          <div className="text-white font-semibold">{money(playerBonus.cash_balance)}</div>
        </div>

        <div>
          <div className="text-gray-400 mb-1">{t("bonus2.players.wageringProgress")}</div>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-700 rounded-full h-2">
              {/* NB: the Tailwind config REPLACES `purple` with a single custom token, so the
                  numbered shades (bg-purple-600 etc.) don't exist and render with no color —
                  the bar looked empty at any %. Use `bg-purple` (the configured token). */}
              <div className="bg-purple h-2 rounded-full transition-all" style={{ width: `${Math.min(wageringPercent, 100)}%` }} />
            </div>
            <span className="text-white font-semibold">{wageringPercent.toFixed(1)}%</span>
          </div>
          <div className="text-gray-400 text-xs mt-1">
            {money(playerBonus.wagering_completed)} / {money(playerBonus.wagering_required)}
          </div>
        </div>

        <div>
          <div className="text-gray-400 mb-1">{t("bonus2.players.activated")}</div>
          <div className="text-white text-xs">{playerBonus.activated_at ? formatDateTime(playerBonus.activated_at) : (t("bonus2.players.notYet") as string)}</div>
        </div>

        <div>
          <div className="text-gray-400 mb-1">{t("bonus2.players.expires2")}</div>
          <div className="text-white text-xs">{formatDateTime(playerBonus.expires_at)}</div>
        </div>

        <div>
          <div className="text-gray-400 mb-1">{t("bonus2.players.coin")}</div>
          <div className="text-white font-semibold">{playerBonus.coin}</div>
        </div>
      </div>

      <FreeSpinsSection playerBonus={playerBonus} gamesCache={gamesCache} />

      {playerBonus.max_cashout && (
        <div className="mt-3 pt-3 border-t border-gray-700">
          <span className="text-gray-400 text-sm">{t("bonus2.players.maxCashout")} </span>
          <span className="text-white font-semibold">{money(playerBonus.max_cashout)}</span>
        </div>
      )}
    </div>
  )
}
